// React Imports
import React, { useState, useCallback, useEffect, useMemo } from 'react';

// MUI Materials Imports
import CircularProgress from '@mui/material/CircularProgress';
import { BiGift, BiCopy, BiShareAlt } from "react-icons/bi";
import { HiOutlineUserAdd, HiOutlineUserRemove } from "react-icons/hi";

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

// Stylers
import './style.scss';

// Profile Components
import ProfileTabs from '../../components/tabs';
import { defaultBanner, getProfileBasics, saveToFollowing, removeFromFollowing, bannerName } from '../../services/profile';
import { authenticatedUserAddress, isUserSignedIn } from '../../services/auth';
import BannerUpload from './BannerUpload';
import { useDropzone } from 'react-dropzone';
import { deleteUploads, save } from '../../services/stacks';

import { Alert, Snackbar } from '@mui/material';

// Components imports
import NFTAvatar from '../../components/avatar/NFTAvatar';
import ConfirmModal from '../../components/modal/ConfirmModal';
import ClaimOGBadge from '../../components/avatar/ClaimOGBadge';
import GiftingModal from '../../components/modal/GiftingModal';
import WoopsModal from '../../components/modal/WoopsModal';
import RequirementErrorModal from '../../components/modal/RequirementErrorModal'; // Corrected import statement
import { clientNetwork } from '../../lib/constants';

const fileReader = new FileReader();
const imageReader = new Image();
const appOrigin = window.location.origin;

const Profile = ({ address, userSettings }) => {
  const [avatar, setAvatar] = useState(null);
  const [authorName, setAuthorName] = useState(null);
  const [authorUser, setAuthorUser] = useState(null);
  const [memberSince, setMemberSince] = useState(null);
  const [bannerUrl, setBannerUrl] = useState(null);
  const [storageUrl, setStorageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [userContacts, setUserContacts] = useState({});
  const [errorMsg, setErrorMsg] = useState({ msg: '', reason: 'success' });
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [txId, setTxId] = useState('');
  
  // Modals state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorStates, setErrorStates] = useState({
      nameCheck: false,
      zonefileCheck: false,
      storageCheck: false,
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showGiftingModal, setShowGiftingModal] = useState(false);
  const [showWoopsModal, setShowWoopsModal] = useState(false);

  const authenticatedUser = useMemo(() => authenticatedUserAddress({ network: clientNetwork }), [clientNetwork]);

  const handleFollowClick = async (authorPrincipal) => {
    if (isUserSignedIn()) {
      saveToFollowing(authorPrincipal, setUserContacts);
      setErrorMsg({ msg: `Now following`, reason: 'success' });
      setOpenSnackBar(true);
    } else {
      setShowWoopsModal(true);
    }
  };

  const handleUnfollowClick = async (authorPrincipal) => {
    if (isUserSignedIn()) {
      removeFromFollowing(authorPrincipal, setUserContacts);
      setErrorMsg({ msg: `Now unfollowing`, reason: 'success' });
      setOpenSnackBar(true);
    } else {
      setShowWoopsModal(true);
    }
  };

  const handleGiftClick = () => {
    if (isUserSignedIn()) {
      setShowGiftingModal(true);
    } else {
      setShowWoopsModal(true);
    }
  };

  const handleCopyClick = (user) => {
    navigator.clipboard.writeText(user);
    setErrorMsg({ msg: `Principal Copied`, reason: 'success' });
    setOpenSnackBar(true);
  };

  const handleShareClick = (user) => {
    navigator.clipboard.writeText(`${appOrigin}/${user}`);
    setErrorMsg({ msg: `Profile Url Copied`, reason: 'success' });
    setOpenSnackBar(true);
  };

  const init = useCallback(async () => {
    try {
      const res = await getProfileBasics(address || authenticatedUser);
      const { address: authedUser, name, deployDate, FuserContacts, userStorage } = res;
      setAuthorUser(authedUser);
      setAuthorName(name);
      setMemberSince(deployDate);
      setUserContacts(FuserContacts);
      setStorageUrl(userStorage);
      setBannerUrl(userStorage ? userStorage + bannerName : defaultBanner);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setIsLoading(false);
    }
  }, [address, authenticatedUser]);

  useEffect(() => {
    if (!authorUser && !isUserSignedIn()) {
      setIsLoading(false);
      return;
    }
    init();
  }, [init, authorUser]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    acceptedFiles.forEach((file) => {
      const fileName = bannerName;
      fileReader.onload = () => {
        imageReader.src = fileReader.result;
        imageReader.onload = () => {
          if (imageReader.width === 1500 && imageReader.height === 500) {
            fileReader.onload = () => {
              const content = fileReader.result;
              deleteUploads(bannerName);
              setIsSaving(true);
              save({ content, fileName }, { encrypt: false }).then(({ result, success }) => {
                if (success) {
                  setBannerUrl(result);
                  setIsSaving(false);
                } else {
                  setErrorMsg({ msg: `Error:  Banner upload failed`, reason: 'error' });
                  setOpenSnackBar(true);
                  setIsSaving(false);
                }
                if (rejectedFiles.length > 0) {
                  setErrorMsg({ msg: `Error: Banner should be .PNG`, reason: 'error' });
                  setOpenSnackBar(true);
                }
              });
            };
            fileReader.readAsArrayBuffer(file);
          } else {
            setErrorMsg({ msg: `Wrong Dimension, Banner should be 1500 x 500 px`, reason: 'warning' });
            setOpenSnackBar(true);
          }
        };
      };
      fileReader.readAsDataURL(file);
    });
  }, []);
  

  const { getInputProps, open } = useDropzone({
    noKeyboard: true,
    noClick: true,
    onDrop,
    accept: "image/png",
    maxFiles: 1,
    maxSize: 20971520
  });

  return (
    <>
      <div className="profile-box">
        {isLoading && <CircularProgress color="success" />}
        {!isLoading && !authorUser && <div>User Account Not Found!</div>}

        {!isLoading && authorUser && (
          <div className='profile-content'>
            <div className="profile-banner">
              <div className="profile-banner-box">
                <img className="profile-banner-image" src={bannerUrl} alt="banner" />
              </div>

              <div className='profile-info-box'>
                <div className='profile-info'>
                  <h1 className="profile-name">{authorName || "Not Available"}</h1>
                  <p className="profile-address">{authorUser.substring(0, 5) + ' ... ' + authorUser.substring(authorUser.length - 5)} </p>
                  <p className="profile-address">Joined:<small> {memberSince}</small></p>
                </div>

                <div className='profile-buttons'>
                  {authorUser !== authenticatedUser
                    ? <div className="action-buttons">
                      {userContacts[authorUser]
                        ? <button className="action-button" onClick={() => handleUnfollowClick(authorUser)}><HiOutlineUserRemove /> </button>
                        : <button className="action-button" onClick={() => handleFollowClick(authorUser)}><HiOutlineUserAdd /> </button>
                      }
                      <button className="action-button" onClick={() => handleCopyClick(authorUser)}><BiCopy /> </button>
                      <button className="action-button" onClick={() => handleGiftClick(authorUser)}><BiGift /> </button>
                      <button className="action-button" onClick={() => handleShareClick(authorUser)}><BiShareAlt /> </button>
                    </div>
                    : null
                  }

                  <BannerUpload 
                    self={authorUser === authenticatedUser} 
                    getInputProps={getInputProps} 
                    open={open} 
                    authorName={authorName}
                    setErrorStates={setErrorStates}
                    setShowErrorModal={setShowErrorModal}
                  />
                </div>
                
              </div>

              <div className="avatar-box">
                <div className="profile-picture">
                                        {/* <AdvancedDeruptar txId={setTxId} setShowConfirmModal={setShowConfirmModal} authorPrincipal={authorUser} /> */}
                  <NFTAvatar 
                    txId={setTxId} 
                    setShowConfirmModal={setShowConfirmModal} 
                    authorPrincipal={authorUser} 
                    AppStoragePath={storageUrl} 
                    authorName={authorName} 
                    userSettings={userSettings} 
                  />

                  <div id="ogcheck-profile-container">
                    <ClaimOGBadge 
                      txId={txId} 
                      setShowConfirmModal={setShowConfirmModal} 
                      authorPrincipal={authorUser} 
                      userSettings={userSettings} 
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='profile-tabs'>
              <ProfileTabs authorPrincipal={authorUser} userSettings={userSettings} />
            </div>
          </div>
        )}
      </div>

      {showErrorModal && (
        <RequirementErrorModal
          show={showErrorModal}
          close={() => setShowErrorModal(false)}
          errors={errorStates}
        />
      )}
      
      {showConfirmModal && (
        <ConfirmModal 
          show={showConfirmModal} 
          txId={txId} 
          close={() => setShowConfirmModal(false)} 
          userSettings={userSettings} 
        />
      )}

      {showGiftingModal && (
        <GiftingModal 
          show={showGiftingModal} 
          setShow={setShowGiftingModal} 
          close={() => setShowGiftingModal(false)} 
          authorPrincipal={authorUser} 
          giftVia={address} 
          userSettings={userSettings} 
        />
      )}

      {showWoopsModal && (
        <WoopsModal 
          show={showWoopsModal} 
          setShow={setShowWoopsModal} 
          close={() => setShowWoopsModal(false)} 
        />
      )}

      <Snackbar open={openSnackBar} autoHideDuration={30000} onClose={() => setOpenSnackBar(false)}>
        <Alert onClose={() => setOpenSnackBar(false)} severity={errorMsg.reason} sx={{ width: '100%' }}>
          {errorMsg.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Profile;
